import { getAllClinicsQuery } from '@/api/clinics/clinicQueries';
import type { UsersFilters } from '@/api/users/usersQueries';
import { getAllUsersQuery } from '@/api/users/usersQueries';
import { Combobox } from '@/components/common/Combobox';
import { Input } from '@/components/common/Input';
import { Role } from '@/domain/Role';
import { useHasRole } from '@/hooks/useHasRole';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface ShowPatientsFiltersProps {
  filters: UsersFilters;
  onFiltersChange: (filters: UsersFilters) => void;
}

export function ShowPatientsFilters({
  filters,
  onFiltersChange,
}: ShowPatientsFiltersProps) {
  const [nameSearch, setNameSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');

  const { hasRole } = useHasRole();
  const isAdmin = hasRole(Role.SHOP_ADMIN) || hasRole(Role.SUPERADMIN);

  const { data: doctors } = useQuery({
    ...getAllUsersQuery({ role: 'Doctor' }),
    enabled: isAdmin,
  });

  const { data: clinics } = useQuery({
    ...getAllClinicsQuery(),
    enabled: isAdmin,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange({ ...filters, search: nameSearch || undefined });
    }, 500);
    return () => clearTimeout(timer);
  }, [nameSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange({ ...filters, email: emailSearch || undefined });
    }, 500);
    return () => clearTimeout(timer);
  }, [emailSearch]);

  const handleDoctorChange = (value: string | undefined) => {
    onFiltersChange({
      ...filters,
      doctor_id: value ? Number(value) : undefined,
    });
  };

  const handleClinicChange = (value: string | undefined) => {
    onFiltersChange({
      ...filters,
      clinic_id: value ? Number(value) : undefined,
    });
  };

  const doctorItems = (doctors ?? []).map((d) => ({
    id: d.id,
    value: `${d.name} ${d.surname}`,
    label: `${d.name} ${d.surname}`,
  }));

  const clinicItems = (clinics ?? []).map((c) => ({
    id: c.id,
    value: c.name,
    label: c.name,
  }));

  return (
    <div className="space-y-4 pt-3">
      <div className="flex flex-wrap gap-4">
        <div className="w-56">
          <Input
            id="name-filter"
            label="Filter by Name"
            placeholder="Filter by Name"
            className="bg-white"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
          />
        </div>
        <div className="w-56">
          <Input
            id="email-filter"
            label="Filter by Email"
            placeholder="Filter by Email"
            className="bg-white"
            value={emailSearch}
            onChange={(e) => setEmailSearch(e.target.value)}
          />
        </div>
        {isAdmin && (
          <>
            <div className="w-56">
              <Combobox
                id="doctor-filter"
                label="Filter by Doctor"
                placeholder="Filter by Doctor"
                items={doctorItems}
                value={filters.doctor_id}
                onChange={handleDoctorChange}
                emptyText="No doctors found."
                className="bg-white"
              />
            </div>
            <div className="w-56">
              <Combobox
                id="clinic-filter"
                label="Filter by Clinic"
                placeholder="Filter by Clinic"
                items={clinicItems}
                value={filters.clinic_id}
                onChange={handleClinicChange}
                emptyText="No clinics found."
                className="bg-white"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
